/* eslint-disable react-hooks/exhaustive-deps */
import { useParams } from "react-router-dom";
import { Navigation } from "../../components/navigation";
import {
  FacebookShareButton,
  FacebookIcon,
  RedditShareButton,
  RedditIcon,
  PinterestShareButton,
  PinterestIcon,
} from "react-share";
import {
  MediaImageFit,
  useMediaImageUrl,
  SizesEnum,
} from "../../components/MediaImage";
import { Artwork, useArtwork } from "../../client";
import { styled } from "../../stitches.config";

const ArtworkTitle = styled("div", {
  color: "#fca903",
  fontFamily: "$serif",
  fontSize: "1.5em",
  boxSizing: "border-box",
});

const ArtworkInfo = styled("div", {
  display: "inline",
});

const FooterContainer = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  flexShrink: 1,
  padding: "20px 30px",
  maxWidth: "720px",
  margin: "auto",

  // Mobile content styles
  position: "relative",
  zIndex: 123,
  backgroundColor: "rgba(0,0,0,0.85)",
});

const ShareContainer = styled("div", {
  height: "30px",
});

export const Share = ({ artwork }: { artwork: Artwork }) => {
  const shareUrl = document.location.href;
  const mediaUrl = useMediaImageUrl({
    artwork,
    fit: MediaImageFit.COVER,
  });
  const size = "30px";
  return (
    <ShareContainer>
      <FacebookShareButton url={shareUrl}>
        <FacebookIcon size={size} />
      </FacebookShareButton>
      <RedditShareButton url={shareUrl}>
        <RedditIcon size={size} />
      </RedditShareButton>
      <PinterestShareButton url={shareUrl} media={mediaUrl}>
        <PinterestIcon size={size} />
      </PinterestShareButton>
    </ShareContainer>
  );
};

export const ArtworkFooter = (props: {
  className?: string;
  artwork: Artwork;
  displayImageIndex: string | number;
}) => {
  const { artwork, className } = props;
  if (!artwork) {
    return null;
  }

  return (
    <FooterContainer className={className}>
      <div>
        <ArtworkTitle>{artwork?.title}</ArtworkTitle>
        <ArtworkInfo>
          {artwork?.materials} - {artwork?.year}
        </ArtworkInfo>
      </div>
      <Share artwork={artwork} />
    </FooterContainer>
  );
};

const LightboxWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "visible",
  paddingTop: "20px",
});

export const LightboxSection = ({
  artwork,
  displayImageIndex,
  imageSize,
  className,
}: {
  artwork: Artwork;
  displayImageIndex: string | number;
  imageSize: SizesEnum;
  fit: MediaImageFit;
  className?: string;
}) => {
  return (
    <LightboxWrapper>
      <img
        src={useMediaImageUrl({ artwork, size: imageSize })}
        alt={artwork.title}
        style={{ maxHeight: "800px" }}
      />
    </LightboxWrapper>
  );
};

export const ArtworkPageContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

export interface ArtworkPageProps {
  artwork?: Artwork;
  id: string;
  displayImageIndex: string;
}

export const ArtworkPage = ({
  id,
  displayImageIndex,
  artwork,
}: ArtworkPageProps) => {
  return (
    <ArtworkPageContainer>
      <Navigation />
      <LightboxSection
        artwork={artwork}
        displayImageIndex={displayImageIndex}
        fit={MediaImageFit.COVER}
        imageSize={SizesEnum.Large}
      />
      <ArtworkFooter artwork={artwork} displayImageIndex={displayImageIndex} />
    </ArtworkPageContainer>
  );
};

export const ArtworkRouter = () => {
  const { id = null } = useParams() as {
    id: string;
    displayImageIndex: string;
  };
  const [artwork] = useArtwork(id);
  const pageProps = {
    artwork,
    id,
  } as ArtworkPageProps;
  if (!artwork) {
    return null;
  }
  return <ArtworkPage {...pageProps} />;
};

export default ArtworkRouter;
